import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTypingText } from "hooks/useTypingText/";
import { useStepTimer } from "hooks/useStepTimer/";
import { useTranslate } from "@refinedev/core";
import { jitter } from "utils/jitter/";
import { AutopilotStep, DotState } from "./types";
import {
  ChevronBtn,
  DescriptionText,
  DialogBox,
  DialogThinking,
  DialogTop,
  Skeleton,
  StepContent,
  StepMeta,
  StepRow,
  StepSpacer,
  StepTitle,
  ThinkingText,
  StoryboardWarningText,
  TimelineCol,
  TimelineDot,
  TimelineTrack,
  TimerBadge,
} from "./Autopilot.styled";
import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { ObjectiveCardsVisual } from "./ObjectiveCardsVisual";
import { AudienceVisual } from "./AudienceVisual";
import { PaletteVisual } from "./PaletteVisual";
import { ScriptVisual } from "./ScriptVisual";
import { TemplatesVisual } from "./TemplatesVisual";
import { VisualsVisual } from "./VisualsVisual";
import { VoiceoverVisual } from "./VoiceOverVisual";
import { ScenesVisual } from "./ScenesVisual";
import { useBrandKit } from "hooks/useBrandKit/";
import { SectionWithSlides } from "hooks/useStoryboardWithSlides/";
import { Templates } from "pages/media/components/storyboardEditor/types/";

interface StepItemProps {
  step: AutopilotStep;
  index: number;
  isActive: boolean;
  thinkingProcess: string[];
  sections: SectionWithSlides[];
  voiceoverSections: SectionWithSlides[];
  objectives: string[] | null;
  brandKitData: ReturnType<typeof useBrandKit>;
  templates: Templates;
  mediaAspectRatio: number;
  isDone: boolean;
  isLast: boolean;
  onComplete: () => void;
  isStoryboardError?: boolean;
  isPromptProject?: boolean;
  autoScrollAllowed?: { readonly current: boolean };
  stepRef?: (el: HTMLDivElement | null) => void;
}

export const StepItem: React.FC<StepItemProps> = ({
  step,
  index,
  isActive,
  thinkingProcess,
  sections,
  voiceoverSections,
  objectives,
  brandKitData,
  templates,
  mediaAspectRatio,
  isDone,
  isLast,
  onComplete,
  isStoryboardError,
  isPromptProject,
  autoScrollAllowed,
  stepRef,
}) => {
  const t = useTranslate();
  const [expanded, setExpanded] = useState(false);
  const [showDialog, setShowDialog] = useState(isDone);
  const [thinkingIndex, setThinkingIndex] = useState(0);
  const [minAnimationDone, setMinAnimationDone] = useState(false);
  const completedRef = useRef(false);

  const safeComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  }, [onComplete]);

  const usePromptVariant = isPromptProject && step.type === "thinking-process";
  const label = t(
    usePromptVariant
      ? "media.autopilot.steps.thinkingProcess.promptLabel"
      : step.labelKey
  );
  const shortDesc = t(
    usePromptVariant
      ? "media.autopilot.steps.thinkingProcess.promptShortDesc"
      : step.shortDescKey
  );
  const longDesc = usePromptVariant
    ? undefined
    : step.longDescKey
      ? t(step.longDescKey)
      : undefined;

  // Captures whether step was already done at mount — never changes.
  // Steps mounted as done show full text; steps becoming done mid-typing
  // continue their animation to completion.
  const [wasInitiallyDone] = useState(isDone);

  const typedShortDesc = useTypingText(
    shortDesc,
    showDialog && !wasInitiallyDone
  );
  const displayedShortDesc = wasInitiallyDone ? shortDesc : typedShortDesc;
  const shortDescDone =
    wasInitiallyDone || typedShortDesc.length >= shortDesc.length;

  const typedLongDesc = useTypingText(
    longDesc ?? "",
    !wasInitiallyDone && showDialog && shortDescDone
  );
  const displayedLongDesc = wasInitiallyDone ? (longDesc ?? "") : typedLongDesc;
  const longDescDone =
    wasInitiallyDone || !longDesc || typedLongDesc.length >= longDesc.length;
  const messageDone = shortDescDone && longDescDone;

  const currentThinkingItem = thinkingProcess[thinkingIndex] ?? "";

  const handleThinkingItemComplete = useCallback(() => {
    if (!isActive || isDone) return;
    if (thinkingIndex < thinkingProcess.length - 1) {
      setThinkingIndex((i) => i + 1);
    } else {
      safeComplete();
    }
  }, [isActive, isDone, thinkingIndex, thinkingProcess.length, safeComplete]);

  const typedThinkingItem = useTypingText(
    currentThinkingItem,
    isActive &&
      step.type === "thinking-process" &&
      currentThinkingItem.length > 0,
    handleThinkingItemComplete
  );

  const timerLabel = useStepTimer(isActive, isDone);
  const dotState: DotState = isDone ? "done" : isActive ? "active" : "pending";

  useEffect(() => {
    if (!isActive) return;
    const t1 = setTimeout(() => setShowDialog(true), 500);
    return () => clearTimeout(t1);
  }, [isActive]);

  const showVisual = isDone || (isActive && shortDescDone);

  const dataReady = useMemo(() => {
    switch (step.readyWhen) {
      case "brandKitReady":
        return !!brandKitData.brandKitPalette;
      case "objectivesReady":
        return !!objectives && objectives.length > 0;
      case "sectionsReady":
        return sections.length > 0;
      case "templatesReady":
        return templates.size > 0;
      default:
        return true;
    }
  }, [
    step.readyWhen,
    brandKitData.brandKitPalette,
    objectives,
    sections.length,
    templates.size,
  ]);

  const MIN_STEP_DISPLAY_MS = 5000;
  // Prompt projects don't render thinking items, so the thinking-process step
  // should use a timer-based completion (delayMs) instead of typing through items.
  const isTimerStep =
    (step.type !== "thinking-process" || isPromptProject) &&
    step.type !== "visuals" &&
    step.type !== "voiceover" &&
    step.type !== "scenes";

  // Both timers start when the visual appears (after typing finishes),
  // ensuring the full text animation plays before any completion can fire.
  // Steps with readyWhen: max timer only unlocks minAnimationDone (data gate stays).
  // Steps without readyWhen: max timer calls safeComplete directly (pure timeout).
  const stepTimerStartRef = useRef<number | null>(null);
  const jitteredDelayRef = useRef<number>(0);

  useEffect(() => {
    if (!isActive || !isTimerStep || !showVisual) return;

    const hasDataGate = !!step.readyWhen;
    const delay = jitter(step.delayMs);
    jitteredDelayRef.current = delay;
    stepTimerStartRef.current = Date.now();

    const maxTimer = setTimeout(
      hasDataGate ? () => setMinAnimationDone(true) : safeComplete,
      delay
    );
    const minTimer = setTimeout(
      () => setMinAnimationDone(true),
      MIN_STEP_DISPLAY_MS
    );

    return () => {
      clearTimeout(maxTimer);
      clearTimeout(minTimer);
    };
  }, [isActive, showVisual]); // eslint-disable-line react-hooks/exhaustive-deps

  // Catch up on step timers after the tab was in the background
  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        isActive &&
        !isDone &&
        isTimerStep &&
        stepTimerStartRef.current !== null
      ) {
        const elapsed = Date.now() - stepTimerStartRef.current;
        if (elapsed >= MIN_STEP_DISPLAY_MS) {
          setMinAnimationDone(true);
        }
        if (elapsed >= jitteredDelayRef.current) {
          const hasDataGate = !!step.readyWhen;
          if (hasDataGate) {
            setMinAnimationDone(true);
          } else {
            safeComplete();
          }
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [isActive, isDone, isTimerStep, step.readyWhen, safeComplete]);

  // Complete when min visual time elapsed AND next step's data is ready
  useEffect(() => {
    if (minAnimationDone && dataReady) safeComplete();
  }, [minAnimationDone, dataReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderVisual = () => {
    if (!showVisual) return null;
    switch (step.type) {
      case "objective-cards":
        return (
          <ObjectiveCardsVisual
            isActive={isActive}
            isDone={isDone}
            objectives={objectives}
          />
        );
      case "audience":
        return <AudienceVisual isActive={isActive} />;
      case "palette":
        return (
          <PaletteVisual
            isActive={isActive}
            isDone={isDone}
            brandKitData={brandKitData}
          />
        );
      case "script":
        return (
          <ScriptVisual
            isActive={isActive}
            shortDescDone={shortDescDone}
            sections={sections}
            autoScrollAllowed={autoScrollAllowed}
          />
        );
      case "templates":
        return <TemplatesVisual isActive={isActive} isDone={isDone} />;
      case "visuals":
        return (
          <VisualsVisual
            isActive={isActive}
            isDone={isDone}
            onComplete={safeComplete}
            sections={sections}
          />
        );
      case "voiceover":
        return (
          <VoiceoverVisual
            isActive={isActive}
            isDone={isDone}
            onComplete={safeComplete}
            sections={voiceoverSections}
            autoScrollAllowed={autoScrollAllowed}
          />
        );
      case "scenes":
        return (
          <ScenesVisual
            isActive={isActive}
            isDone={isDone}
            onComplete={safeComplete}
            sections={sections}
            templates={templates}
            brandKitData={brandKitData}
            mediaAspectRatio={mediaAspectRatio}
            autoScrollAllowed={autoScrollAllowed}
          />
        );
      default:
        return null;
    }
  };

  const completedThinkingItems = isDone
    ? thinkingProcess
    : thinkingProcess.slice(0, thinkingIndex);

  return (
    <StepRow ref={stepRef}>
      <TimelineCol>
        <TimelineDot $state={dotState} />
        {!isLast && <TimelineTrack $state={dotState} />}
      </TimelineCol>

      <StepContent>
        <StepMeta>
          {t("media.autopilot.stepPrefix")} {String(index + 1).padStart(2, "0")}
        </StepMeta>
        <StepTitle>{label}</StepTitle>

        {isActive && !showDialog && <Skeleton />}

        {showDialog && (
          <DialogBox>
            <DialogTop
              $clickable={isDone}
              $active={isActive && !isDone}
              onClick={() => isDone && setExpanded((e) => !e)}
            >
              <DescriptionText>{displayedShortDesc}</DescriptionText>
              {(isActive || isDone) && (
                <TimerBadge $done={isDone}>{timerLabel}</TimerBadge>
              )}
              {(isActive || isDone) && (
                <ChevronBtn>
                  {isActive || expanded ? (
                    <CaretUpIcon size={15} />
                  ) : (
                    <CaretDownIcon size={15} />
                  )}
                </ChevronBtn>
              )}
            </DialogTop>
            {(isActive || (isDone && expanded)) &&
              step.type === "thinking-process" &&
              !isPromptProject &&
              (thinkingProcess.length > 0 || isStoryboardError) && (
                <DialogThinking $active={isActive && !isDone}>
                  {isStoryboardError && thinkingProcess.length === 0 && (
                    <StoryboardWarningText>
                      {t("media.autopilot.storyboardError.title")}.{" "}
                      {t("media.autopilot.storyboardError.description")}
                    </StoryboardWarningText>
                  )}
                  {completedThinkingItems.map((item, i) => (
                    <ThinkingText
                      key={i}
                      style={i > 0 ? { marginTop: 12 } : undefined}
                    >
                      {item}
                    </ThinkingText>
                  ))}
                  {!isDone && thinkingIndex < thinkingProcess.length && (
                    <ThinkingText
                      style={
                        completedThinkingItems.length > 0
                          ? { marginTop: 12 }
                          : undefined
                      }
                    >
                      {typedThinkingItem}
                    </ThinkingText>
                  )}
                </DialogThinking>
              )}
            {(isActive || (isDone && expanded)) &&
              longDesc &&
              step.type !== "thinking-process" && (
                <DialogThinking $active={isActive && !isDone}>
                  <ThinkingText>{displayedLongDesc}</ThinkingText>
                </DialogThinking>
              )}
          </DialogBox>
        )}

        {renderVisual()}

        {isActive && !isDone && showDialog && <ThinkingIndicator />}

        <StepSpacer />
      </StepContent>
    </StepRow>
  );
};
